import { supabase } from '../lib/supabase';

// Helper functions for case conversion
const toCamelCase = (str) => {
  return str?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
};

const toSnakeCase = (str) => {
  return str?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
};

const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const convertKeysToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = convertKeysToSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// User Profile Service
export const userProfileService = {
  async getCurrentUserProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: null };

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async updateProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      const { data, error } = await supabase?.from('user_profiles')?.update(snakeCaseUpdates)?.eq('id', user?.id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async getTrainerClients(trainerId) {
    try {
      const { data, error } = await supabase?.from('pt_client_assignments')?.select(`
          *,
          client:user_profiles!pt_client_assignments_client_id_fkey(*)
        `)?.eq('trainer_id', trainerId)?.eq('status', 'active');

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async getClientTrainer(clientId) {
    try {
      const { data, error } = await supabase?.from('pt_client_assignments')?.select(`
          *,
          trainer:user_profiles!pt_client_assignments_trainer_id_fkey(*)
        `)?.eq('client_id', clientId)?.eq('status', 'active')?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  }
};

// Exercise Service
export const exerciseService = {
  async getExercises(filters = {}) {
    try {
      let query = supabase?.from('exercises')?.select('*')?.eq('is_public', true);

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }
      if (filters?.difficulty) {
        query = query?.eq('difficulty', filters?.difficulty);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async createExercise(exercise) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseExercise = convertKeysToSnakeCase({ ...exercise, createdBy: user?.id });
      const { data, error } = await supabase?.from('exercises')?.insert(snakeCaseExercise)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async updateExercise(id, updates) {
    try {
      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      const { data, error } = await supabase?.from('exercises')?.update(snakeCaseUpdates)?.eq('id', id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async deleteExercise(id) {
    try {
      const { error } = await supabase?.from('exercises')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { error };
      }

      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error };
    }
  }
};

// Workout Plan Service
export const workoutPlanService = {
  async getWorkoutPlans(trainerId) {
    try {
      const { data, error } = await supabase?.from('workout_plans')?.select(`
          *,
          workout_plan_exercises(
            *,
            exercise:exercises(*)
          )
        `)?.eq('created_by', trainerId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async createWorkoutPlan(plan, exercises) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCasePlan = convertKeysToSnakeCase({ ...plan, createdBy: user?.id });
      const { data: planData, error: planError } = await supabase?.from('workout_plans')?.insert(snakeCasePlan)?.select()?.single();

      if (planError) {
        if (isSchemaError(planError)) throw planError;
        return { data: null, error: planError };
      }

      if (exercises && exercises?.length > 0) {
        const planExercises = exercises?.map((ex, index) => ({
          workout_plan_id: planData?.id,
          exercise_id: ex?.exerciseId,
          order_index: index + 1,
          sets: ex?.sets,
          reps: ex?.reps,
          duration_seconds: ex?.durationSeconds,
          rest_seconds: ex?.restSeconds,
          notes: ex?.notes
        }));

        const { error: exercisesError } = await supabase?.from('workout_plan_exercises')?.insert(planExercises);

        if (exercisesError) {
          if (isSchemaError(exercisesError)) throw exercisesError;
          return { data: convertKeysToCamelCase(planData), error: exercisesError };
        }
      }

      return { data: convertKeysToCamelCase(planData), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async assignWorkoutToClient(workoutPlanId, clientId, scheduledDate) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const { data, error } = await supabase?.from('assigned_workouts')?.insert({
          workout_plan_id: workoutPlanId,
          client_id: clientId,
          trainer_id: user?.id,
          scheduled_date: scheduledDate,
          status: 'scheduled'
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async getAssignedWorkouts(userId, role) {
    try {
      let query = supabase?.from('assigned_workouts')?.select(`
          *,
          workout_plan:workout_plans(*),
          client:user_profiles!assigned_workouts_client_id_fkey(full_name, email),
          trainer:user_profiles!assigned_workouts_trainer_id_fkey(full_name, email)
        `);

      if (role === 'client') {
        query = query?.eq('client_id', userId);
      } else {
        query = query?.eq('trainer_id', userId);
      }

      query = query?.order('scheduled_date', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async updateWorkoutStatus(workoutId, status) {
    try {
      const updates = { status };
      if (status === 'completed') {
        updates.completed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('assigned_workouts')?.update(updates)?.eq('id', workoutId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async updateWorkoutPlan(planId, updates, exercises) {
    try {
      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      const { data: planData, error: planError } = await supabase?.from('workout_plans')?.update(snakeCaseUpdates)?.eq('id', planId)?.select()?.single();

      if (planError) {
        if (isSchemaError(planError)) throw planError;
        return { data: null, error: planError };
      }

      if (exercises) {
        await supabase?.from('workout_plan_exercises')?.delete()?.eq('workout_plan_id', planId);

        if (exercises?.length > 0) {
          const planExercises = exercises?.map((ex, index) => ({
            workout_plan_id: planId,
            exercise_id: ex?.exerciseId,
            order_index: index + 1,
            sets: ex?.sets,
            reps: ex?.reps,
            duration_seconds: ex?.durationSeconds,
            rest_seconds: ex?.restSeconds,
            notes: ex?.notes
          }));

          const { error: exercisesError } = await supabase?.from('workout_plan_exercises')?.insert(planExercises);

          if (exercisesError) {
            if (isSchemaError(exercisesError)) throw exercisesError;
            return { data: convertKeysToCamelCase(planData), error: exercisesError };
          }
        }
      }

      return { data: convertKeysToCamelCase(planData), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async deleteWorkoutPlan(planId) {
    try {
      const { error } = await supabase?.from('workout_plans')?.delete()?.eq('id', planId);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { error };
      }

      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error };
    }
  }
};

// Message Service
export const messageService = {
  async getConversation(userId1, userId2) {
    try {
      const { data, error } = await supabase?.from('messages')?.select('*')?.or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)?.order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async sendMessage(recipientId, content) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const { data, error } = await supabase?.from('messages')?.insert({
          sender_id: user?.id,
          recipient_id: recipientId,
          content,
          status: 'sent'
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async markAsRead(messageId) {
    try {
      const { data, error } = await supabase?.from('messages')?.update({ status: 'read', read_at: new Date()?.toISOString() })?.eq('id', messageId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  subscribeToMessages(userId, callback) {
    const channel = supabase?.channel('messages_changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          callback(convertKeysToCamelCase(payload?.new));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  subscribeToMessageUpdates(userId, callback) {
    const channel = supabase?.channel('message_updates')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          callback(convertKeysToCamelCase(payload?.new));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// Real-time Subscription Service
export const realtimeService = {
  // Subscribe to assigned workout changes (for Trainer Dashboard)
  subscribeToAssignedWorkouts(trainerId, callback) {
    const channel = supabase?.channel('assigned_workouts_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assigned_workouts',
          filter: `trainer_id=eq.${trainerId}`
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: convertKeysToCamelCase(payload?.new || payload?.old)
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  // Subscribe to workout logs (for Performance Analytics)
  subscribeToWorkoutLogs(trainerId, clientIds, callback) {
    const channels = clientIds?.map(clientId => {
      return supabase?.channel(`workout_logs_${clientId}`)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'workout_logs',
            filter: `client_id=eq.${clientId}`
          },
          (payload) => {
            callback({
              event: payload?.eventType,
              data: convertKeysToCamelCase(payload?.new)
            });
          }
        )?.subscribe();
    });

    return () => {
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  },

  // Subscribe to steps logs (for Performance Analytics adherence data)
  subscribeToStepsLogs(clientIds, callback) {
    const channels = clientIds?.map(clientId => {
      return supabase?.channel(`steps_logs_${clientId}`)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'steps_logs',
            filter: `client_id=eq.${clientId}`
          },
          (payload) => {
            callback({
              event: payload?.eventType,
              data: convertKeysToCamelCase(payload?.new)
            });
          }
        )?.subscribe();
    });

    return () => {
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  },

  // Subscribe to progress photos (for Trainer Dashboard)
  subscribeToProgressPhotos(clientIds, callback) {
    const channels = clientIds?.map(clientId => {
      return supabase?.channel(`progress_photos_${clientId}`)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'progress_photos',
            filter: `client_id=eq.${clientId}`
          },
          (payload) => {
            callback({
              event: payload?.eventType,
              data: convertKeysToCamelCase(payload?.new)
            });
          }
        )?.subscribe();
    });

    return () => {
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  },

  // Subscribe to progress notes (for Trainer Dashboard)
  subscribeToProgressNotes(clientIds, callback) {
    const channels = clientIds?.map(clientId => {
      return supabase?.channel(`progress_notes_${clientId}`)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'progress_notes',
            filter: `client_id=eq.${clientId}`
          },
          (payload) => {
            callback({
              event: payload?.eventType,
              data: convertKeysToCamelCase(payload?.new)
            });
          }
        )?.subscribe();
    });

    return () => {
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  }
};

// Progress Photo Service
export const progressPhotoService = {
  async uploadPhoto(file, metadata) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('progress-photos')?.upload(fileName, file);

      if (uploadError) {
        if (isSchemaError(uploadError)) throw uploadError;
        return { data: null, error: uploadError };
      }

      const { data: urlData, error: urlError } = await supabase?.storage?.from('progress-photos')?.createSignedUrl(fileName, 31536000);

      if (urlError) {
        if (isSchemaError(urlError)) throw urlError;
        return { data: null, error: urlError };
      }

      const { data, error } = await supabase?.from('progress_photos')?.insert({
          client_id: user?.id,
          photo_url: fileName,
          caption: metadata?.caption,
          weight: metadata?.weight,
          body_fat_percentage: metadata?.bodyFatPercentage,
          measurements: metadata?.measurements,
          taken_at: metadata?.takenAt || new Date()?.toISOString()?.split('T')?.[0]
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: { ...convertKeysToCamelCase(data), signedUrl: urlData?.signedUrl }, error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async getProgressPhotos(clientId) {
    try {
      const { data, error } = await supabase?.from('progress_photos')?.select('*')?.eq('client_id', clientId)?.order('taken_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      const photosWithUrls = await Promise.all(
        data?.map(async (photo) => {
          const { data: urlData } = await supabase?.storage?.from('progress-photos')?.createSignedUrl(photo?.photo_url, 3600);
          
          return {
            ...convertKeysToCamelCase(photo),
            signedUrl: urlData?.signedUrl
          };
        })
      );

      return { data: photosWithUrls, error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async deletePhoto(photoId, photoUrl) {
    try {
      const { error: storageError } = await supabase?.storage?.from('progress-photos')?.remove([photoUrl]);

      if (storageError) {
        if (isSchemaError(storageError)) throw storageError;
      }

      const { error } = await supabase?.from('progress_photos')?.delete()?.eq('id', photoId);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { error };
      }

      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error };
    }
  }
};

// Food Log Service
export const foodLogService = {
  async getFoodLogs(clientId, startDate, endDate) {
    try {
      let query = supabase?.from('food_logs')?.select('*')?.eq('client_id', clientId);

      if (startDate) {
        query = query?.gte('logged_at', startDate);
      }
      if (endDate) {
        query = query?.lte('logged_at', endDate);
      }

      query = query?.order('logged_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async addFoodLog(foodLog) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseFoodLog = convertKeysToSnakeCase({ ...foodLog, clientId: user?.id });
      const { data, error } = await supabase?.from('food_logs')?.insert(snakeCaseFoodLog)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async deleteFoodLog(id) {
    try {
      const { error } = await supabase?.from('food_logs')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { error };
      }

      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error };
    }
  }
};

// Steps Log Service
export const stepsLogService = {
  async getStepsLogs(clientId, startDate, endDate) {
    try {
      let query = supabase?.from('steps_logs')?.select('*')?.eq('client_id', clientId);

      if (startDate) {
        query = query?.gte('logged_date', startDate);
      }
      if (endDate) {
        query = query?.lte('logged_date', endDate);
      }

      query = query?.order('logged_date', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async addStepsLog(stepsLog) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseStepsLog = convertKeysToSnakeCase({ ...stepsLog, clientId: user?.id });
      const { data, error } = await supabase?.from('steps_logs')?.insert(snakeCaseStepsLog)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  }
};

// PT Client Service
export const ptClientService = {
  async getMyClients(trainerId) {
    try {
      const { data, error } = await supabase?.from('pt_client_assignments')?.select(`
          *,
          client:user_profiles!pt_client_assignments_client_id_fkey(*)
        `)?.eq('trainer_id', trainerId)?.eq('status', 'active')?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  }
};

export const progressNotesService = {
  async getProgressNotes(clientId) {
    try {
      const { data, error } = await supabase?.from('progress_notes')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], error: null };
        return { data: [], error };
      }

      return { data: data?.map(note => convertKeysToCamelCase(note)), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: [], error };
    }
  },

  async addProgressNote(noteData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const { data, error } = await supabase?.from('progress_notes')?.insert({
          client_id: noteData?.clientId,
          title: noteData?.title,
          content: noteData?.content,
          tags: noteData?.tags || []
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { data: null, error };
    }
  },

  async deleteProgressNote(noteId) {
    try {
      const { error } = await supabase?.from('progress_notes')?.delete()?.eq('id', noteId);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { error };
      }

      return { error: null };
    } catch (error) {
      if (isSchemaError(error)) throw error;
      return { error };
    }
  }
};